import { useEffect, useState } from 'react';
import { Input } from '../ui/index.ts';

/**
 * Inline-edit text field: type, then blur or press Enter to submit.
 *
 * There is no optimistic write — the caller's PATCH lands and React Query
 * invalidates before the displayed value changes. Sluggish on slow links,
 * but the field never shows a value the server has not accepted.
 *
 * An empty submission reverts rather than clearing: every current consumer
 * edits a required identifier, so "" is a slip, not an intent.
 */
interface Props {
  readonly label: string;
  readonly initial: string;
  readonly testId: string;
  readonly mono?: boolean;
  readonly onSubmit: (value: string) => Promise<void> | void;
}

export function InlineTextField({ label, initial, testId, mono, onSubmit }: Props): JSX.Element {
  const [value, setValue] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setValue(initial);
    setDirty(false);
  }, [initial]);

  async function commit(): Promise<void> {
    if (!dirty) {
      return;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setValue(initial);
      setDirty(false);
      return;
    }
    await onSubmit(trimmed);
    setDirty(false);
  }

  return (
    <label className="flex flex-col gap-2">
      <span className="text-label text-paper-mid">
        {label}
      </span>
      <Input
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
        }}
        data-testid={testId}
        mono={mono === true}
      />
    </label>
  );
}
